import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  ANIMATION_TYPE_OPTIONS,
  EMPTY_STICKER_FORM,
  PACK_STATUS_FORM_OPTIONS,
  VISIBILITY_OPTIONS,
} from './stickerConstants';

export default function StickerAssetEditModal({ open, packId, stickerId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_STICKER_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(stickerId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        if (stickerId) {
          const data = await adminApi.getImSticker(stickerId);
          if (cancelled) {
            return;
          }
          setForm({
            emoteCode: data.emoteCode || '',
            name: data.name || '',
            description: data.description || '',
            objectKey: data.objectKey || '',
            mimeType: data.mimeType || 'image/png',
            width: data.width ?? 240,
            height: data.height ?? 240,
            animationType: data.animationType || 'STATIC',
            subscriptionTier: data.subscriptionTier || '',
            visibility: data.visibility || 'PUBLIC',
            category: data.category || '',
            language: data.language || 'en_US',
            tags: data.tags || '',
            status: data.status || 'ACTIVE',
            sort: data.sort ?? 0,
          });
        } else {
          setForm({ ...EMPTY_STICKER_FORM });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, stickerId]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError('');
    try {
      const key = await adminApi.uploadOssImage(file);
      updateField('objectKey', key);
      if (file.type) {
        updateField('mimeType', file.type);
      }
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!isEdit) {
      const code = form.emoteCode || '';
      if (!/^[A-Za-z0-9_]{1,20}$/.test(code) || code.startsWith('gif_')) {
        setError('Emote Code 須符合 [A-Za-z0-9_]{1,20}，且不可使用 gif_ 前綴（保留給 GIF）');
        return;
      }
    }
    if (!form.objectKey?.trim()) {
      setError('請上傳貼圖');
      return;
    }
    const payload = {
      name: form.name?.trim() || null,
      description: form.description?.trim() || null,
      objectKey: form.objectKey.trim(),
      mimeType: form.mimeType?.trim() || null,
      width: form.width === '' || form.width == null ? null : Number(form.width),
      height: form.height === '' || form.height == null ? null : Number(form.height),
      animationType: form.animationType,
      subscriptionTier: form.subscriptionTier?.trim() || null,
      visibility: form.visibility,
      category: form.category?.trim() || null,
      language: form.language?.trim() || null,
      tags: form.tags?.trim() || null,
      status: form.status,
      sort: Number(form.sort) || 0,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateImSticker(stickerId, payload);
      } else {
        await adminApi.createImSticker(packId, {
          ...payload,
          emoteCode: form.emoteCode.trim(),
        });
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '編輯貼圖' : '新增貼圖'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : (
          <form className="form-grid" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <label>
              Emote Code
              <input
                type="text"
                value={form.emoteCode}
                onChange={(e) => updateField('emoteCode', e.target.value)}
                disabled={isEdit}
                maxLength={20}
                required={!isEdit}
                placeholder="如 formosaOk"
              />
            </label>
            <label>
              名稱
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </label>
            <label>
              狀態
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                {PACK_STATUS_FORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Visibility
              <select
                value={form.visibility}
                onChange={(e) => updateField('visibility', e.target.value)}
              >
                {VISIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Animation
              <select
                value={form.animationType}
                onChange={(e) => updateField('animationType', e.target.value)}
              >
                {ANIMATION_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              排序
              <input
                type="number"
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
              />
            </label>
            <label>
              寬
              <input
                type="number"
                value={form.width ?? ''}
                onChange={(e) => updateField('width', e.target.value)}
              />
            </label>
            <label>
              高
              <input
                type="number"
                value={form.height ?? ''}
                onChange={(e) => updateField('height', e.target.value)}
              />
            </label>
            <label>
              訂閱層級
              <input
                type="text"
                value={form.subscriptionTier}
                onChange={(e) => updateField('subscriptionTier', e.target.value)}
                placeholder="可空＝跟 Pack"
              />
            </label>
            <label>
              標籤
              <input
                type="text"
                value={form.tags}
                onChange={(e) => updateField('tags', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              描述
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              貼圖檔
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                {form.objectKey ? (
                  <img
                    src={resolveAdminMediaUrl(form.objectKey)}
                    alt=""
                    style={{ width: 72, height: 72, objectFit: 'contain' }}
                  />
                ) : null}
              </div>
              <small>{form.objectKey || '尚未上傳'}</small>
            </label>

            <div className="modal-panel__footer form-grid__full">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                取消
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                {submitting ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
